import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Translate } from '../../../../../shared/LanguageService';
import { storeyNames } from '../state/storeyNames';
import { FloorPlanFile } from '../types';

type FloorPlanUploadProps = {
    files: FloorPlanFile[];
    onAddFiles: (files: File[]) => void;
    onChangeStorey: (index: number, storeyId: number) => void;
    onRemoveFile: (index: number) => void;
};

const acceptedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];

const hiddenFileInput: React.CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0
};

function isAccepted(file: File): boolean {
    return acceptedFileTypes.includes(file.type);
}

export function FloorPlanUpload({
    files,
    onAddFiles,
    onChangeStorey,
    onRemoveFile
}: FloorPlanUploadProps) {
    let [isDraggingOver, setIsDraggingOver] = React.useState(false);
    let [hasRefusedFiles, setHasRefusedFiles] = React.useState(false);

    function addFiles(fileList: FileList | null) {
        let chosen = Array.from(fileList || []);
        let accepted = chosen.filter(isAccepted);
        setHasRefusedFiles(accepted.length < chosen.length);
        if (accepted.length > 0) {
            onAddFiles(accepted);
        }
    }

    return (
        <Box
            component="section"
            aria-label={Translate('order/create/v2/floor-plans/upload/title')}
            onDragOver={(event: React.DragEvent) => {
                event.preventDefault();
                setIsDraggingOver(true);
            }}
            onDragLeave={() => setIsDraggingOver(false)}
            onDrop={(event: React.DragEvent) => {
                event.preventDefault();
                setIsDraggingOver(false);
                addFiles(event.dataTransfer.files);
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                padding: '16px',
                textAlign: 'center',
                border: '2px dashed',
                borderColor: isDraggingOver ? 'secondary.main' : 'divider',
                borderRadius: '8px',
                backgroundColor: isDraggingOver ? 'action.hover' : 'transparent'
            }}
        >
            <UploadFileIcon sx={{ fontSize: '32px', color: 'text.secondary' }} />
            <Typography component="h2" sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                {Translate('order/create/v2/floor-plans/upload/title')}
            </Typography>
            <Typography sx={{ fontSize: '13px' }}>
                {Translate('order/create/v2/floor-plans/upload/text')}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {Translate('order/create/v2/floor-plans/upload/drop-here')}
            </Typography>
            <Button variant="contained" color="secondary" component="label">
                {Translate('order/create/v2/floor-plans/upload/choose-files')}
                <input
                    style={hiddenFileInput}
                    multiple
                    type="file"
                    accept={acceptedFileTypes.join(',')}
                    onChange={(event) => {
                        addFiles(event.target.files);
                        event.target.value = '';
                    }}
                />
            </Button>
            {hasRefusedFiles && (
                <Alert severity="warning" sx={{ fontSize: '14px' }}>
                    {Translate('order/create/v2/floor-plans/upload/wrong-file-type')}
                </Alert>
            )}
            {files.length > 0 && (
                <Box
                    component="ul"
                    sx={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        width: '100%',
                        display: 'grid',
                        gap: '8px'
                    }}
                >
                    {files.map((floorPlanFile, index) => (
                        <Paper
                            component="li"
                            key={`${floorPlanFile.file.name}-${index}`}
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 8px 8px 16px',
                                fontSize: '14px',
                                textAlign: 'left'
                            }}
                        >
                            <Box component="span" sx={{ flex: 1, wordBreak: 'break-all' }}>
                                {floorPlanFile.file.name}
                            </Box>
                            <TextField
                                id={`create-order-floor-plan-storey-${index}`}
                                select
                                size="small"
                                sx={{ width: '220px' }}
                                SelectProps={{ native: true }}
                                InputLabelProps={{ shrink: true }}
                                label={Translate('order/create/v2/floor-plans/upload/storey')}
                                value={String(floorPlanFile.storeyId)}
                                onChange={(event) =>
                                    onChangeStorey(index, Number(event.target.value))
                                }
                            >
                                {storeyNames.map((storeyName) => (
                                    <option key={storeyName.id} value={storeyName.id}>
                                        {Translate(storeyName.translationKey)}
                                    </option>
                                ))}
                            </TextField>
                            <IconButton
                                size="small"
                                aria-label={`${Translate(
                                    'order/create/v2/floor-plans/upload/remove'
                                )} ${floorPlanFile.file.name}`}
                                onClick={() => onRemoveFile(index)}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Paper>
                    ))}
                </Box>
            )}
        </Box>
    );
}
